import dayjs from 'dayjs';
import type { ServiceOrder, OrderItem } from '../../api/serviceOrders';
import {
  Draft, DraftLine, displayUnit, isVolumeUnit, newLine,
} from './types';

// Сборка черновика заказ-наряда из ответа API — общая для обоих видов.
// Схема: ServiceOrderPage.md §2В (rev. 2026-09-08).

export const emptyDraft = (): Draft => ({
  vehicle_id: null, vehicle: null, vehicle_make: null, vehicle_model: null,
  license_plate: null, vin: null,
  year: null, color: null, engine_type: null, engine_number: null, body_number: null,
  client_type: null, client_id: null, client_label: null, retail_buyer_name: null,
  mileage: null, notes: '',
  prepayment_sum: null, work_start_date: null, work_end_date: null,
  services: [newLine()], parts: [newLine()],
  payment_type: 'Касса',
  card_payment: true,
  print_receipt: true,
  vat_for_products: true,
  vat_for_services: true,
  bank_terminal: null,
  org_bank_account_id: null,
});

export const itemToLine = (i: OrderItem): DraftLine => ({
  key: `db-${i.external_1c_id || Math.random().toString(36).slice(2)}`,
  external_1c_id: i.external_1c_id ?? null,
  name: i.name,
  quantity: Number(i.quantity),
  unit: displayUnit(i.unit) || 'шт',
  price: Number(i.price),
  sum: i.sum != null ? Number(i.sum) : undefined,
  recommended_price: i.recommended_price ? Number(i.recommended_price) : undefined,
  price_from_price_list: i.price_from_price_list ?? undefined,
  executor1_id: i.executor1_id ?? null,
  executor1_name: i.executor1 ?? null,
  executor2_id: i.executor2_id ?? null,
  executor2_name: i.executor2 ?? null,
  executor3_id: i.executor3_id ?? null,
  executor3_name: i.executor3 ?? null,
  vat: i.vat != null ? Number(i.vat) : undefined,
  vat_rate: i.vat_rate ?? undefined,
  sum_with_vat: i.sum_with_vat != null ? Number(i.sum_with_vat) : undefined,
  norm_hour: i.norm_hour != null ? Number(i.norm_hour) : undefined,
  work_code: i.work_code ?? undefined,
  marking_code: i.marking_code ?? null,
  mark_has_full_code: i.mark_has_full_code ?? null,
  mark_status: i.mark_status ?? null,
  unreceived_pending: i.unreceived_pending ?? undefined,
});

// Группировка позиций из 1С: маркированный товар → мастер-строка + дочерние марки.
// Разливной: объём мастера = сумма дочерних объёмов (rev. 2026-08-10).
// Штучный (rev. 2026-08-14): 1 марка = 1 шт, количество мастера = число дочерних марок.
export const groupPartsToDraft = (items: OrderItem[]): DraftLine[] => {
  const result: DraftLine[] = [];
  const masters = new Map<string, DraftLine>(); // external_1c_id+тип → мастер
  items.forEach(i => {
    const line = itemToLine(i);
    // Отложенные позиции режима «без оприходования» — плоские строки с тегом,
    // в мастера не группируем (unreceived_sales.md v3: строка редактируемая)
    if (line.unreceived_pending) {
      result.push(line);
      return;
    }
    const isBulkMark = !!line.marking_code && isVolumeUnit(line.unit);
    // Штучные группируем только при quantity = 1 (норма после раскладки backend'ом);
    // аномалии (qty > 1 с одной маркой) остаются плоскими строками — без потери количества
    const isPieceMark = !!line.marking_code && !isVolumeUnit(line.unit) && Number(line.quantity) === 1;
    if (!isBulkMark && !isPieceMark) {
      result.push(line);
      return;
    }
    const gk = `${line.external_1c_id || line.name}:${isBulkMark ? 'bulk' : 'piece'}`;
    let master = masters.get(gk);
    if (!master) {
      master = {
        ...line,
        key: `master-${gk}`,
        marking_code: null,
        quantity: 0,
        is_bulk_master: isBulkMark || undefined,
        is_piece_master: isPieceMark || undefined,
        bulk_gtin: isBulkMark && line.marking_code ? line.marking_code.slice(2, 16) : null,
        children: [],
      };
      masters.set(gk, master);
      result.push(master);
    }
    master.quantity = isBulkMark
      ? Number((master.quantity + line.quantity).toFixed(1))
      : master.quantity + 1;
    master.children!.push({
      ...line,
      key: `${master.key}:${line.marking_code}`,
      parent_key: master.key,
      available_volume: null,
    });
  });
  return [...result, newLine()];
};

export const orderToDraft = (o: ServiceOrder): Draft => ({
  vehicle_id: o.vehicle_id ?? null,
  vehicle: o.vehicle ?? null,
  // Марка/модель из 1С не приходят — пикер покажет имя в режиме «Другое»
  vehicle_make: null,
  vehicle_model: null,
  license_plate: o.license_plate ?? null,
  vin: o.vin ?? null,
  year: o.manufacture_year ?? null,
  color: o.color ?? null,
  engine_type: o.engine_type ?? null,
  engine_number: o.engine_number ?? null,
  body_number: o.body_number ?? null,
  client_type: o.client_type ?? null,
  client_id: o.client_id ?? null,
  client_label: o.counterparty ?? o.retail_buyer_name ?? null,
  retail_buyer_name: o.retail_buyer_name ?? null,
  mileage: o.mileage ?? null,
  notes: o.recommendations ?? '',
  prepayment_sum: o.prepayment_sum ?? null,
  work_start_date: o.work_start_date ? dayjs(o.work_start_date) : null,
  work_end_date: o.work_end_date ? dayjs(o.work_end_date) : null,
  services: [...o.items.filter(i => i.item_type === 'service').map(itemToLine), newLine()],
  parts: groupPartsToDraft(o.items.filter(i => i.item_type === 'part')),
  payment_type: (o.payment_type === 'Банк' ? 'Банк' : 'Касса'),
  card_payment: o.card_payment ?? true,
  print_receipt: o.print_receipt ?? true,
  vat_for_products: o.vat_for_products ?? true,
  vat_for_services: o.vat_for_services ?? true,
  bank_terminal: o.bank_terminal ?? null,
  org_bank_account_id: o.org_bank_account_id ?? null,
});
